// src/pages/Inventory/StockCountPage.jsx — الجرد الفعلي
// تُدخل الكمية الفعلية لكل صنف ← الفرق مع رصيد الدفتر:
//   الفعلي أكبر  → تسوية بالزيادة (99 — إدخال)
//   الفعلي أصغر → تسوية بالعجز   (13 — إخراج)
// مثال: الدفتر −20 والفعلي 0 → زيادة 20 فيصبح الرصيد 0
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, MenuItem, InputAdornment, IconButton, Button, Tooltip, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, TablePagination, ToggleButton, ToggleButtonGroup,
  Dialog, DialogContent, DialogActions, FormControlLabel, Checkbox, Alert,
} from '@mui/material';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExposureZeroIcon from '@mui/icons-material/ExposureOutlined';
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import PublishOutlinedIcon from '@mui/icons-material/PublishOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ChecklistIcon from '@mui/icons-material/Checklist';
import BalanceIcon from '@mui/icons-material/Balance';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { toast } from 'react-toastify';
import axios from '../../axios';
import invoiceApi from '../../api/invoiceApi';
import { ReportPage, fieldSx, fmtMoney, BRAND } from '../../components/Reports/ReportKit';

const SHORTAGE_TYPE = 13; // تسوية بالعجز
const INCREASE_TYPE = 99; // تسوية بالزيادة
const PAGE_SIZE = 100;

// أرقام عربية/فارسية → إنجليزية، وإزالة فواصل الآلاف
const cleanNum = (v) => String(v ?? '')
  .replace(/[\u0660-\u0669]/g, d => String(d.charCodeAt(0) - 0x0660))
  .replace(/[\u06F0-\u06F9]/g, d => String(d.charCodeAt(0) - 0x06F0))
  .replace(/[\u066C,\s]/g, '')
  .replace('\u066B', '.');
const num = (v) => { const n = parseFloat(cleanNum(v)); return Number.isFinite(n) ? n : 0; };
const fmtQty = (v) => {
  const n = Math.round((Number(v) || 0) * 1000) / 1000;
  return n.toLocaleString('en-US', { maximumFractionDigits: 3 });
};
const today = () => new Date().toLocaleDateString('en-CA');

const draftKey = (companyId, stockId) => `stockCount:${companyId}:${stockId}`;
const readDraft = (k) => { try { return JSON.parse(localStorage.getItem(k) || '{}') || {}; } catch { return {}; } };
const writeDraft = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* تجاهل */ } };
const clearDraft = (k) => { try { localStorage.removeItem(k); } catch { /* تجاهل */ } };

function purchasePrice(u, p) {
  return parseFloat(u?.purchprice ?? u?.purchaseprice ?? u?.purchPrice ?? p?.purchprice ?? p?.purchaseprice ?? p?.purchPrice ?? 0) || 0;
}

export default function StockCountPage() {
  const navigate = useNavigate();
  const companyId = parseInt(localStorage.getItem('companyId'), 10);
  const branchId = parseInt(localStorage.getItem('branchId'), 10);
  const userId = parseInt(localStorage.getItem('userId'), 10);

  const [stocks, setStocks] = useState([]);
  const [stockId, setStockId] = useState('');
  const [date, setDate] = useState(today());
  const [products, setProducts] = useState([]);
  const [balances, setBalances] = useState({});
  const [counts, setCounts] = useState({}); // productId → النص المُدخل
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [confirm, setConfirm] = useState(false);
  const [result, setResult] = useState(null);
  const inputs = useRef({});

  // ── المستودعات والأصناف ──
  useEffect(() => {
    if (!companyId) return;
    axios.get(`/Stock/by-company/${companyId}`).then(r => {
      const list = Array.isArray(r.data) ? r.data : [];
      setStocks(list);
      const saved = parseInt(localStorage.getItem('stockId'), 10);
      const first = list.find(s => s.id === saved) || list[0];
      if (first) setStockId(first.id);
    }).catch(() => toast.error('تعذر تحميل المستودعات'));
    axios.get(`/Product/by-company/${companyId}`)
      .then(r => setProducts(Array.isArray(r.data) ? r.data : (r.data?.items || r.data?.data || [])))
      .catch(() => toast.error('تعذر تحميل الأصناف'));
  }, [companyId]);

  // ── أرصدة الدفتر ──
  const loadBalances = useCallback(() => {
    if (!companyId || !stockId) return;
    setLoading(true);
    axios.get('/StockCount/balances', { params: { companyId, stockId, asOf: date } })
      .then(r => setBalances(Object.fromEntries((r.data || []).map(b => [b.productId, Number(b.qty) || 0]))))
      .catch(e => toast.error(e?.response?.data?.message || 'تعذر تحميل أرصدة المستودع'))
      .finally(() => setLoading(false));
  }, [companyId, stockId, date]);

  useEffect(() => { loadBalances(); }, [loadBalances]);

  // مسودة الجرد محفوظة على الجهاز لكل مستودع
  useEffect(() => { if (stockId) setCounts(readDraft(draftKey(companyId, stockId))); }, [companyId, stockId]);
  useEffect(() => { if (stockId) writeDraft(draftKey(companyId, stockId), counts); }, [counts, companyId, stockId]);

  // ── الصفوف ──
  const rows = useMemo(() => products
    .filter(p => !p.isService && !p.is_deleted && !p.isDeleted)
    .map(p => {
      const units = Array.isArray(p.units) ? p.units : [];
      const base = units.find(u => u.isBaseUnit) || units[0] || {};
      const book = balances[p.id] ?? 0;
      const raw = counts[p.id];
      const counted = raw !== undefined && String(raw).trim() !== '';
      const actual = counted ? num(raw) : null;
      const diff = counted ? Math.round((actual - book) * 1000) / 1000 : 0;
      const cost = purchasePrice(base, p);
      return {
        id: p.id,
        code: p.barcode ?? p.code ?? p.itemCode ?? p.productCode ?? '',
        name: p.arabicName ?? p.nameAr ?? p.name ?? '',
        unitId: base.unitId ?? 0,
        unitName: base.unitNameAr ?? base.unitName ?? '',
        cost, book, counted, actual, diff,
      };
    }), [products, balances, counts]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    const qn = cleanNum(search).toLowerCase();
    return rows.filter(r => {
      if (q && !(String(r.name).toLowerCase().includes(q) || String(r.code).toLowerCase().includes(qn || q))) return false;
      if (filter === 'counted') return r.counted;
      if (filter === 'uncounted') return !r.counted;
      if (filter === 'diff') return r.counted && r.diff !== 0;
      if (filter === 'negative') return r.book < 0;
      return true;
    });
  }, [rows, search, filter]);

  useEffect(() => { setPage(0); }, [search, filter, stockId]);
  const pageRows = shown.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  const increases = rows.filter(r => r.counted && r.diff > 0);
  const shortages = rows.filter(r => r.counted && r.diff < 0);
  const value = (list) => list.reduce((s, r) => s + Math.abs(r.diff) * r.cost, 0);
  const negatives = rows.filter(r => r.book < 0);

  const stats = [
    { label: 'أصناف مجرودة', value: `${rows.filter(r => r.counted).length} / ${rows.length}`, icon: <ChecklistIcon />, color: 'primary', highlight: true },
    { label: `زيادة (${increases.length} صنف)`, value: fmtMoney(value(increases)), icon: <TrendingUpIcon />, color: 'success' },
    { label: `عجز (${shortages.length} صنف)`, value: fmtMoney(value(shortages)), icon: <TrendingDownIcon />, color: 'error' },
    { label: 'صافي الفرق', value: fmtMoney(value(increases) - value(shortages)), icon: <BalanceIcon />, color: 'slate' },
  ];

  // ── أدوات ──
  const setCount = (id, v) => setCounts(c => ({ ...c, [id]: v }));
  const zeroNegatives = () => {
    if (!negatives.length) { toast.info('لا توجد أصناف برصيد سالب'); return; }
    setCounts(c => { const n = { ...c }; negatives.forEach(r => { n[r.id] = '0'; }); return n; });
    toast.success(`تم تصفير ${negatives.length} صنف رصيده سالب — سيُضاف لكل منها ما يعادل العجز`);
  };
  const clearAll = () => { setCounts({}); toast.info('تم مسح الكميات المدخلة'); };

  const focusRow = (idx) => {
    const el = inputs.current[idx];
    if (el) { el.focus(); el.select(); }
  };

  // ── الترحيل ──
  const buildDoc = (typeId, list, invoiceNumber, paymentMethodId) => {
    const items = list.map(r => ({
      productId: r.id,
      unitId: r.unitId,
      quantity: Math.abs(r.diff),
      unitPrice: r.cost,
      discount: 0,
      taxPercent: 0,
      description: `جرد: دفتري ${fmtQty(r.book)} — فعلي ${fmtQty(r.actual)}`,
    }));
    const total = items.reduce((s, i) => s + i.quantity * i.unitPrice, 0);
    const iso = new Date(date).toISOString();
    return {
      companyId, branchId, userId: userId || 0,
      invoiceTypeId: typeId,
      invoiceNumber,
      issueDate: iso,
      referenceNumber: '',
      referenceDate: iso,
      customerId: 0, supplierId: 0, customerPrevBalance: 0,
      stockId: Number(stockId), toStockId: null,
      salesmanId: null,
      paymentMethodId,
      safeId: 0, bankId: 0,
      notes: `جرد فعلي — ${stocks.find(s => s.id === Number(stockId))?.name ?? ''} — ${date}`,
      discount: 0,
      invoiceStatus: 0,
      paidAmount: 0,
      totals: { subtotal: total, totalAfterDiscount: total, totalVAT: 0, net: total },
      items,
    };
  };

  const post = async (approve) => {
    setResult({ busy: true });
    const created = [];
    try {
      // طريقة دفع افتراضية (حقل مطلوب في الفاتورة) من إعدادات نوع التسوية
      const settings = await axios.get('/ProgramSettings/by-company-and-type', { params: { companyId, invoiceTypeId: SHORTAGE_TYPE } })
        .then(r => r.data).catch(() => null);
      let paymentMethodId = settings?.defaultPaymentMethodId;
      if (!paymentMethodId) {
        const methods = await axios.get(`/PaymentMethod/by-company/${companyId}`).then(r => r.data).catch(() => []);
        paymentMethodId = methods?.[0]?.id ?? 0;
      }

      for (const [typeId, list] of [[SHORTAGE_TYPE, shortages], [INCREASE_TYPE, increases]]) {
        if (!list.length) continue;
        const nr = await invoiceApi.getNextNumber(companyId, branchId, typeId);
        const number = (nr.data && typeof nr.data === 'object' && nr.data.displayNumber) ? nr.data.displayNumber : String(nr.data ?? '');
        const res = await invoiceApi.create(buildDoc(typeId, list, number, paymentMethodId));
        created.push({ typeId, id: res.data?.invGlobalID ?? res.data?.invGlobalId, count: list.length, value: value(list) });
      }

      // الاعتماد بعد نجاح الإنشاء كاملًا (الاعتماد هو الذي يحرّك المخزون)
      if (approve) for (const c of created) { await invoiceApi.approve(c.id); c.approved = true; }

      clearDraft(draftKey(companyId, stockId));
      setCounts({});
      setResult({ docs: created });
      loadBalances();
    } catch (e) {
      // إنشاء غير مكتمل: نحذف المسودات التي أُنشئت حتى لا يبقى نصف جرد
      for (const c of created.filter(x => !x.approved)) {
        await axios.delete(`/Invoice/${c.id}`).catch(() => {});
      }
      const d = e?.response?.data;
      toast.error(typeof d === 'string' ? d : d?.message || d?.title || 'تعذر ترحيل الجرد');
      setResult(created.some(x => x.approved) ? { docs: created.filter(x => x.approved), partial: true } : null);
    }
  };

  const canPost = (increases.length + shortages.length) > 0 && !!stockId;
  const cellInputSx = {
    width: 110,
    '& .MuiOutlinedInput-root': { borderRadius: '9px', bgcolor: '#fff', height: 34 },
    '& input': { textAlign: 'center', fontWeight: 800, fontVariantNumeric: 'tabular-nums', direction: 'ltr', py: 0 },
  };
  const th = { fontWeight: 800, color: '#475569', bgcolor: '#f8fafc', whiteSpace: 'nowrap', borderBottom: '1px solid #e5e7eb' };

  return (
    <ReportPage
      title="الجرد الفعلي"
      subtitle="أدخل الكمية الفعلية — الفرق يُرحَّل تلقائيًا تسوية بالزيادة أو بالعجز"
      icon={<FactCheckOutlinedIcon />}
      actions={[
        { label: `تصفير السالب (${negatives.length})`, icon: <ExposureZeroIcon />, onClick: zeroNegatives, disabled: !negatives.length },
        { label: 'مسح', icon: <DeleteSweepOutlinedIcon />, onClick: clearAll, disabled: !Object.keys(counts).length },
        { label: 'ترحيل الجرد', icon: <PublishOutlinedIcon />, onClick: () => setConfirm(true), primary: true, disabled: !canPost },
      ]}
      stats={stats}
    >
      <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        {/* شريط الأدوات */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', p: 1.25, borderBottom: '1px solid #e5e7eb', bgcolor: '#fafbfc', flexWrap: 'wrap' }}>
          <TextField select size="small" label="المستودع" value={stockId} onChange={e => setStockId(e.target.value)} sx={{ ...fieldSx, minWidth: 180 }}>
            {stocks.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
          </TextField>
          <TextField size="small" type="date" label="تاريخ الجرد" value={date} onChange={e => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }} sx={{ ...fieldSx, width: 165 }} />
          <TextField size="small" value={search} onChange={e => setSearch(e.target.value)} placeholder="بحث بالاسم أو الباركود..."
            sx={{ ...fieldSx, flex: '1 1 220px' }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment>,
              endAdornment: search ? <InputAdornment position="end"><IconButton size="small" onClick={() => setSearch('')}><CloseIcon fontSize="small" /></IconButton></InputAdornment> : null,
            }} />
          <ToggleButtonGroup exclusive size="small" value={filter} onChange={(_, v) => v && setFilter(v)}
            sx={{ '& .MuiToggleButton-root': { px: 1.25, fontWeight: 700, fontSize: 12.5, borderRadius: '9px !important', border: '1px solid #e5e7eb !important', mx: .2 },
                  '& .Mui-selected': { bgcolor: `${BRAND.primarySoft} !important`, color: `${BRAND.primary} !important` } }}>
            <ToggleButton value="all">الكل</ToggleButton>
            <ToggleButton value="uncounted">غير مجرود</ToggleButton>
            <ToggleButton value="counted">مجرود</ToggleButton>
            <ToggleButton value="diff">فروقات</ToggleButton>
            <ToggleButton value="negative">سالب</ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="تحديث الأرصدة"><span>
            <IconButton onClick={loadBalances} disabled={loading} sx={{ border: '1px solid #e5e7eb', borderRadius: '10px' }}><RefreshIcon /></IconButton>
          </span></Tooltip>
        </Box>

        {loading && !products.length ? (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}><CircularProgress sx={{ color: BRAND.primary }} /></Box>
        ) : (
          <Box sx={{ overflowX: 'auto', maxHeight: { md: '62vh' } }}>
            <Table size="small" stickyHeader sx={{ '& td, & th': { textAlign: 'center', py: .75 } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...th, width: 120 }}>الباركود</TableCell>
                  <TableCell sx={{ ...th, textAlign: 'right !important' }}>الصنف</TableCell>
                  <TableCell sx={{ ...th, width: 80 }}>الوحدة</TableCell>
                  <TableCell sx={{ ...th, width: 110 }}>الدفتري</TableCell>
                  <TableCell sx={{ ...th, width: 130, color: BRAND.primary }}>الفعلي</TableCell>
                  <TableCell sx={{ ...th, width: 110 }}>الفرق</TableCell>
                  <TableCell sx={{ ...th, width: 100 }}>التكلفة</TableCell>
                  <TableCell sx={{ ...th, width: 120 }}>قيمة الفرق</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pageRows.map((r, idx) => {
                  const up = r.diff > 0, down = r.diff < 0;
                  return (
                    <TableRow key={r.id} hover sx={{ bgcolor: up ? '#f0fdf4' : down ? '#fff1f2' : undefined }}>
                      <TableCell sx={{ fontFamily: 'monospace', color: '#64748b', fontSize: 12.5 }}>{r.code || '—'}</TableCell>
                      <TableCell sx={{ textAlign: 'right !important', fontWeight: 700 }}>{r.name}</TableCell>
                      <TableCell sx={{ color: '#64748b' }}>{r.unitName || '—'}</TableCell>
                      <TableCell sx={{ fontWeight: 800, direction: 'ltr', color: r.book < 0 ? '#e11d48' : '#0f172a' }}>{fmtQty(r.book)}</TableCell>
                      <TableCell>
                        <TextField size="small" value={counts[r.id] ?? ''} placeholder="—"
                          inputRef={el => { inputs.current[idx] = el; }}
                          inputProps={{ inputMode: 'decimal', 'aria-label': `الكمية الفعلية ${r.name}` }}
                          onFocus={e => e.target.select()}
                          onChange={e => { const v = cleanNum(e.target.value); if (/^-?\d*\.?\d*$/.test(v)) setCount(r.id, v); }}
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === 'ArrowDown') { e.preventDefault(); focusRow(idx + 1); }
                            if (e.key === 'ArrowUp') { e.preventDefault(); focusRow(idx - 1); }
                            if (e.key === 'Escape') setCount(r.id, '');
                          }}
                          sx={{ ...cellInputSx, '& .MuiOutlinedInput-root': { ...cellInputSx['& .MuiOutlinedInput-root'], bgcolor: r.counted ? BRAND.primarySoft : '#fff' } }} />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 800, direction: 'ltr', color: up ? '#059669' : down ? '#e11d48' : '#94a3b8' }}>
                        {r.counted ? `${up ? '+' : ''}${fmtQty(r.diff)}` : '—'}
                      </TableCell>
                      <TableCell sx={{ color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(r.cost)}</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: up ? '#059669' : down ? '#e11d48' : '#94a3b8' }}>
                        {r.counted && r.diff !== 0 ? fmtMoney(Math.abs(r.diff) * r.cost) : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!pageRows.length && (
                  <TableRow><TableCell colSpan={8} sx={{ py: 6, color: '#94a3b8' }}>{products.length ? 'لا توجد أصناف مطابقة' : 'لا توجد أصناف'}</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        )}
        {shown.length > PAGE_SIZE && (
          <TablePagination component="div" count={shown.length} page={page} onPageChange={(_, p) => setPage(p)}
            rowsPerPage={PAGE_SIZE} rowsPerPageOptions={[PAGE_SIZE]} labelDisplayedRows={({ from, to, count }) => `${from}–${to} من ${count}`}
            sx={{ borderTop: '1px solid #e5e7eb', direction: 'ltr' }} />
        )}
      </Paper>
      <Typography sx={{ fontSize: 12.5, color: '#94a3b8', mt: 1 }}>
        الأصناف التي تُترك فارغة لا تدخل في الجرد. Enter ينتقل للصنف التالي، Esc يمسح الخانة. الكميات بالوحدة الأساسية، وتُحفظ مسودة الجرد على هذا الجهاز حتى الترحيل.
      </Typography>

      {/* تأكيد الترحيل */}
      <ConfirmDialog open={confirm} onClose={() => setConfirm(false)}
        increases={increases} shortages={shortages} value={value}
        onPost={(approve) => { setConfirm(false); post(approve); }} />

      {/* النتيجة */}
      <Dialog open={!!result} onClose={() => !result?.busy && setResult(null)} dir="rtl" maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '18px' } }}>
        <DialogContent sx={{ display: 'grid', gap: 1.5, pt: 3 }}>
          {result?.busy ? (
            <Box sx={{ display: 'grid', placeItems: 'center', gap: 1, py: 3 }}>
              <CircularProgress sx={{ color: BRAND.primary }} />
              <Typography sx={{ color: '#64748b' }}>جاري ترحيل الجرد...</Typography>
            </Box>
          ) : (
            <>
              <Typography sx={{ fontWeight: 800, fontSize: 17 }}>{result?.partial ? 'تم ترحيل جزء من الجرد' : 'تم ترحيل الجرد'}</Typography>
              {result?.docs?.map(d => (
                <Box key={d.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.25, borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                  {d.typeId === INCREASE_TYPE ? <TrendingUpIcon sx={{ color: '#059669' }} /> : <TrendingDownIcon sx={{ color: '#e11d48' }} />}
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 800 }}>{d.typeId === INCREASE_TYPE ? 'تسوية بالزيادة' : 'تسوية بالعجز'}</Typography>
                    <Typography sx={{ fontSize: 12.5, color: '#64748b' }}>{d.count} صنف · {fmtMoney(d.value)} · {d.approved ? 'معتمدة' : 'مسودة'}</Typography>
                  </Box>
                  <Tooltip title="فتح المستند">
                    <IconButton onClick={() => navigate(`/invoices/edit/${d.id}?invoiceTypeId=${d.typeId}`)} sx={{ color: BRAND.primary }}><OpenInNewIcon /></IconButton>
                  </Tooltip>
                </Box>
              ))}
            </>
          )}
        </DialogContent>
        {!result?.busy && (
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button variant="contained" onClick={() => setResult(null)} sx={{ borderRadius: '10px', fontWeight: 800, background: BRAND.buttonGradient }}>تم</Button>
          </DialogActions>
        )}
      </Dialog>
    </ReportPage>
  );
}

function ConfirmDialog({ open, onClose, increases, shortages, value, onPost }) {
  const [approve, setApprove] = useState(true);
  const Line = ({ icon, color, label, list }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: 1.25, borderRadius: '12px', bgcolor: list.length ? `${color}0f` : '#f8fafc', border: `1px solid ${list.length ? `${color}33` : '#eef0f4'}` }}>
      <Box sx={{ color, display: 'flex' }}>{icon}</Box>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontWeight: 800 }}>{label}</Typography>
        <Typography sx={{ fontSize: 12.5, color: '#64748b' }}>{list.length ? `${list.length} صنف` : 'لا يوجد'}</Typography>
      </Box>
      <Typography sx={{ fontWeight: 800, color, fontVariantNumeric: 'tabular-nums' }}>{list.length ? fmtMoney(value(list)) : '—'}</Typography>
    </Box>
  );
  return (
    <Dialog open={open} onClose={onClose} dir="rtl" maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '18px', overflow: 'hidden' } }}>
      <Box sx={{ px: 2.5, py: 1.5, color: '#fff', background: BRAND.headerGradient, display: 'flex', alignItems: 'center', gap: 1 }}>
        <PublishOutlinedIcon />
        <Typography sx={{ fontWeight: 800, fontSize: 16, flex: 1 }}>ترحيل الجرد</Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
      </Box>
      <DialogContent sx={{ display: 'grid', gap: 1.25, pt: '16px !important' }}>
        <Line icon={<TrendingUpIcon />} color="#059669" label="تسوية بالزيادة (إدخال)" list={increases} />
        <Line icon={<TrendingDownIcon />} color="#e11d48" label="تسوية بالعجز (إخراج)" list={shortages} />
        <FormControlLabel control={<Checkbox checked={approve} onChange={e => setApprove(e.target.checked)} />}
          label={<Typography sx={{ fontWeight: 700, fontSize: 14 }}>اعتماد المستندات مباشرة (تحريك المخزون الآن)</Typography>} />
        {!approve && <Alert severity="info" sx={{ borderRadius: '12px', py: 0 }}>تُحفظ مسودات، ولا يتحرك المخزون حتى تعتمدها.</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: '1px solid #eef0f4' }}>
        <Button variant="text" onClick={onClose} sx={{ fontWeight: 700, color: '#64748b', bgcolor: 'transparent', '&:hover': { bgcolor: '#f1f5f9' } }}>إلغاء</Button>
        <Button variant="contained" onClick={() => onPost(approve)} startIcon={<PublishOutlinedIcon />}
          sx={{ borderRadius: '10px', fontWeight: 800, px: 3, background: BRAND.buttonGradient, '& .MuiButton-startIcon': { ml: .75, mr: -.25 } }}>ترحيل</Button>
      </DialogActions>
    </Dialog>
  );
}
